// Native-only "saved login" support for the iOS/Android app.
//
// Two independent mechanisms, both fed after a successful manual login:
//  1. Device biometrics (Face ID / Touch ID): email + password are stored in
//     the device Keychain/Keystore via @capgo/capacitor-native-biometric, and
//     a "Log in with Face ID" button on the login form retrieves them after a
//     successful biometric check.
//  2. iCloud Keychain: @capgo/capacitor-autofill-save-password shows the
//     system "Save password?" dialog so the credential also autofills in
//     Safari and other apps (relies on the webcredentials Associated Domain).
//
// Everything is a silent no-op on web and on devices without biometrics.

import { Capacitor } from '@capacitor/core';
// Static import on purpose: this was previously a dynamic import(), but the
// lazy-loaded chunk silently never resolved inside the native WKWebView,
// which stalled every biometric call with no error. The module itself is a
// tiny registerPlugin() proxy — inert on web until a method is called.
import { NativeBiometric } from '@capgo/capacitor-native-biometric';

// Keychain "server" key the credentials are filed under. Must stay stable —
// changing it orphans previously saved logins.
const CREDENTIAL_SERVER = 'pedalxchange.com';

// Only nag the iCloud Keychain "Save password?" dialog once per email.
const SAVE_PW_PROMPTED_KEY = 'pxc_icloud_pw_prompted';

// Our own record that setCredentials succeeded, used only as a fallback when
// the plugin's isCredentialsSaved can't give a definitive answer.
const BIO_SAVED_MARKER_KEY = 'pxc_biometric_login_saved';

// Which email the (single) saved login belongs to, so the login screen can
// show which account the Face ID button signs into. Display-only — the
// actual credentials live in the Keychain/Keystore.
const BIO_SAVED_EMAIL_KEY = 'pxc_biometric_login_email';

const isNative = () => Capacitor.isNativePlatform();

function hasLocalSaveMarker() {
  try {
    return localStorage.getItem(BIO_SAVED_MARKER_KEY) === '1';
  } catch {
    return false;
  }
}

function setLocalSaveMarker(value, email) {
  try {
    if (value) {
      localStorage.setItem(BIO_SAVED_MARKER_KEY, '1');
      if (email) localStorage.setItem(BIO_SAVED_EMAIL_KEY, email);
    } else {
      localStorage.removeItem(BIO_SAVED_MARKER_KEY);
      localStorage.removeItem(BIO_SAVED_EMAIL_KEY);
    }
  } catch {
    // best-effort only
  }
}

/**
 * The email of the saved Face ID / Touch ID login, or null if unknown.
 * Only one login is stored at a time (the most recent password login), so
 * the login screen uses this to show which account the button signs into.
 */
export function getSavedBiometricEmail() {
  try {
    return localStorage.getItem(BIO_SAVED_EMAIL_KEY) || null;
  } catch {
    return null;
  }
}

// A native bridge call that never settles would otherwise stall callers with
// no error and no logs. The Face ID scan gets a longer budget since the
// system prompt (and first-run permission alert) needs user interaction.
function withTimeout(promise, label, ms = 4000) {
  return Promise.race([
    promise,
    new Promise((_, reject) =>
      setTimeout(() => reject(new Error(`${label} timed out (${ms}ms)`)), ms)
    ),
  ]);
}

/**
 * Human-friendly name of the device's biometric method, or null if
 * biometrics are unavailable / not enrolled / not native.
 */
export async function getBiometricLabel() {
  if (!isNative()) return null;
  try {
    const result = await withTimeout(NativeBiometric.isAvailable(), 'isAvailable');
    if (!result.isAvailable) return null;
    // BiometryType enum: 1 = Touch ID, 2 = Face ID (iOS);
    // 3+ = fingerprint/face/iris (Android).
    if (result.biometryType === 2) return 'Face ID';
    if (result.biometryType === 1) return 'Touch ID';
    return 'biometrics';
  } catch (err) {
    console.warn('Biometric availability check failed:', err?.message);
    return null;
  }
}

/**
 * True when biometrics work on this device AND a login was previously saved,
 * i.e. the "Log in with Face ID" button should be shown.
 */
export async function hasSavedBiometricLogin() {
  if (!isNative()) return false;
  try {
    const avail = await withTimeout(NativeBiometric.isAvailable(), 'isAvailable');
    if (!avail.isAvailable) return false;
    const saved = await withTimeout(
      NativeBiometric.isCredentialsSaved({ server: CREDENTIAL_SERVER }),
      'isCredentialsSaved'
    );
    // isCredentialsSaved is the source of truth, but fall back to our own
    // marker if the plugin can't answer (older native builds return undefined).
    return saved?.isSaved === undefined ? hasLocalSaveMarker() : !!saved.isSaved;
  } catch (err) {
    console.warn('Saved-login check failed, using local marker:', err?.message);
    return hasLocalSaveMarker();
  }
}

/** Store the login in the device Keychain/Keystore for future biometric logins. */
export async function saveBiometricLogin(email, password) {
  if (!isNative() || !email || !password) return false;
  try {
    const avail = await withTimeout(NativeBiometric.isAvailable(), 'isAvailable');
    if (!avail.isAvailable) return false;
    // Delete before saving: setCredentials with a *different* username adds a
    // second keychain entry instead of replacing (the plugin only treats the
    // same username as a duplicate), and reads then keep returning the oldest
    // entry — i.e. Face ID would keep logging into the previous account.
    await withTimeout(
      NativeBiometric.deleteCredentials({ server: CREDENTIAL_SERVER }),
      'deleteCredentials'
    ).catch(() => { /* nothing saved yet — fine */ });
    await withTimeout(
      NativeBiometric.setCredentials({
        username: email,
        password,
        server: CREDENTIAL_SERVER,
      }),
      'setCredentials'
    );
    setLocalSaveMarker(true, email);
    return true;
  } catch (err) {
    console.warn('Biometric credential save failed (non-critical):', err?.message);
    return false;
  }
}

/**
 * Run the Face ID / Touch ID check and return the saved { email, password },
 * or null if the user cancelled or nothing is saved.
 */
export async function getBiometricLogin() {
  if (!isNative()) return null;

  try {
    // Generous timeout: the system prompt needs user interaction, but a
    // native call that never settles must not leave the login button stuck.
    await withTimeout(
      NativeBiometric.verifyIdentity({
        reason: 'Log in to PedalxChange',
        title: 'Log in to PedalxChange',
      }),
      'verifyIdentity',
      30000
    );
  } catch {
    // User cancelled or failed the Face ID / Touch ID check — stay silent.
    return null;
  }

  try {
    const creds = await withTimeout(
      NativeBiometric.getCredentials({ server: CREDENTIAL_SERVER }),
      'getCredentials'
    );
    if (!creds?.username || !creds?.password) {
      setLocalSaveMarker(false);
      return null;
    }
    // Backfill the display email for logins saved before it was recorded.
    setLocalSaveMarker(true, creds.username);
    return { email: creds.username, password: creds.password };
  } catch (err) {
    // Identity was confirmed but the Keychain has nothing for us (e.g. cleared
    // on reinstall). Drop the marker so the button hides until the next login.
    console.warn('Biometric credential read failed:', err?.message);
    setLocalSaveMarker(false);
    return null;
  }
}

/** Remove the saved login (stale password, or account deletion). */
export async function clearBiometricLogin() {
  if (!isNative()) return;
  setLocalSaveMarker(false);
  try {
    await withTimeout(
      NativeBiometric.deleteCredentials({ server: CREDENTIAL_SERVER }),
      'deleteCredentials'
    );
  } catch {
    // Nothing saved — fine.
  }
}

/**
 * Show the iOS "Save password in iCloud Keychain?" dialog (once per email).
 * Fire-and-forget: never blocks the login flow.
 */
export function maybePromptSavePasswordToKeychain(email, password) {
  if (!isNative() || Capacitor.getPlatform() !== 'ios' || !email || !password) return;
  try {
    const prompted = JSON.parse(localStorage.getItem(SAVE_PW_PROMPTED_KEY) || '[]');
    if (prompted.includes(email)) return;
    import('@capgo/capacitor-autofill-save-password')
      .then(({ SavePassword }) =>
        SavePassword.promptDialog({ username: email, password, url: CREDENTIAL_SERVER })
      )
      .then(() => {
        localStorage.setItem(SAVE_PW_PROMPTED_KEY, JSON.stringify([...prompted, email]));
      })
      .catch(() => {
        // iCloud save-password prompt unavailable — non-critical.
      });
  } catch {
    // localStorage unavailable — skip rather than re-nag forever.
  }
}
